import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { OrgSwitcher } from "@/components/org-switcher";
import { Plus, Send, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { sendCampaign, submitCampaign, approveCampaign, rejectCampaign } from "@/lib/sms.functions";

export const Route = createFileRoute("/_authenticated/campaigns")({ component: CampaignsPage });

const CHANNELS = ["sms", "ussd", "whatsapp", "email", "voice", "push"] as const;
const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_approval: "bg-amber-500/20 text-amber-300",
  approved: "bg-emerald-500/20 text-emerald-300",
  rejected: "bg-destructive/20 text-destructive-foreground",
  scheduled: "bg-blue-500/20 text-blue-300",
  sending: "bg-yellow-500/20 text-yellow-300",
  completed: "bg-green-500/20 text-green-300",
  failed: "bg-destructive/20 text-destructive-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

function CampaignsPage() {
  const { orgId } = useCurrentOrg();
  const [rows, setRows] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", channel: "sms" as (typeof CHANNELS)[number], message: "", subject: "", templateId: "", scheduledFor: "" });
  const [busy, setBusy] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const send = useServerFn(sendCampaign);
  const submit = useServerFn(submitCampaign);
  const approve = useServerFn(approveCampaign);
  const reject = useServerFn(rejectCampaign);

  async function load() {
    if (!orgId) return;
    const [c, t, u] = await Promise.all([
      supabase.from("campaigns").select("*").eq("organization_id", orgId).order("created_at", { ascending: false }),
      supabase.from("message_templates").select("id,name,channel,body,subject").eq("organization_id", orgId),
      supabase.auth.getUser(),
    ]);
    setRows(c.data ?? []);
    setTemplates(t.data ?? []);
    if (u.data.user) {
      const { data: admin } = await supabase.rpc("is_org_admin", { _org: orgId, _user: u.data.user.id });
      setIsAdmin(!!admin);
    }
  }
  useEffect(() => { load(); }, [orgId]);

  function applyTemplate(id: string) {
    const t = templates.find((x) => x.id === id);
    if (!t) return setForm({ ...form, templateId: "" });
    setForm({ ...form, templateId: id, message: t.body, subject: t.subject ?? form.subject, channel: t.channel });
  }

  async function save() {
    if (!orgId) return;
    const { data: u } = await supabase.auth.getUser();
    const { error } = await supabase.from("campaigns").insert({
      organization_id: orgId, name: form.name, channel: form.channel, message: form.message,
      subject: form.subject || null, created_by: u.user?.id, status: "draft",
      template_id: form.templateId || null,
      scheduled_for: form.scheduledFor ? new Date(form.scheduledFor).toISOString() : null,
    });
    if (error) return toast.error(error.message);
    toast.success("Campaign saved as draft");
    setOpen(false); setForm({ name: "", channel: "sms", message: "", subject: "", templateId: "", scheduledFor: "" });
    load();
  }

  async function run(id: string, fn: () => Promise<any>, okMsg: string) {
    setBusy(id);
    try { await fn(); toast.success(okMsg); load(); }
    catch (e: any) { toast.error(e?.message ?? "Action failed"); }
    finally { setBusy(null); }
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Campaigns</h1>
          <p className="text-muted-foreground text-sm mt-1">Draft → submit → approve → dispatch. Schedule for later or send now.</p>
        </div>
        <div className="flex gap-2">
          <OrgSwitcher />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New campaign</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create campaign</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div>
                  <Label>Channel</Label>
                  <Select value={form.channel} onValueChange={(v: any) => setForm({ ...form, channel: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{CHANNELS.map((c) => <SelectItem key={c} value={c}>{c.toUpperCase()}{c === "sms" ? " (live)" : " (draft only)"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {templates.filter((t) => t.channel === form.channel).length > 0 && (
                  <div>
                    <Label>Template (optional)</Label>
                    <Select value={form.templateId || "none"} onValueChange={(v) => applyTemplate(v === "none" ? "" : v)}>
                      <SelectTrigger><SelectValue placeholder="No template" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No template</SelectItem>
                        {templates.filter((t) => t.channel === form.channel).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.channel === "email" && (
                  <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></div>
                )}
                <div>
                  <Label>Message</Label>
                  <Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={1000} />
                  <div className="text-xs text-muted-foreground mt-1">{form.message.length} chars</div>
                </div>
                <div>
                  <Label>Schedule for (optional)</Label>
                  <Input type="datetime-local" value={form.scheduledFor} onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })} />
                  <div className="text-xs text-muted-foreground mt-1">Leave empty to send on approval. Cron dispatches scheduled, approved campaigns every minute.</div>
                </div>
                <Button onClick={save} className="w-full" disabled={!form.name || !form.message}>Save as draft</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Name</TableHead><TableHead>Channel</TableHead><TableHead>Status</TableHead>
              <TableHead>Schedule</TableHead><TableHead>Sent</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">
                    {c.name}
                    {c.rejection_reason && <div className="text-xs text-destructive mt-1">Rejected: {c.rejection_reason}</div>}
                  </TableCell>
                  <TableCell><Badge variant="outline">{c.channel.toUpperCase()}</Badge></TableCell>
                  <TableCell><span className={`text-xs px-2 py-1 rounded ${STATUS_COLORS[c.status] ?? ""}`}>{c.status}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{c.scheduled_for ? new Date(c.scheduled_for).toLocaleString() : "—"}</TableCell>
                  <TableCell>{(c.stats?.sent ?? 0)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    {c.status === "draft" && (
                      <Button size="sm" variant="outline" onClick={() => run(c.id, () => submit({ data: { campaignId: c.id } }), "Submitted for approval")} disabled={busy === c.id}>
                        <ClipboardCheck className="h-3 w-3 mr-1" />Submit
                      </Button>
                    )}
                    {c.channel === "sms" && c.status === "draft" && (
                      <Button size="sm" variant="ghost" onClick={() => run(c.id, () => send({ data: { campaignId: c.id } }), "Dispatched")} disabled={busy === c.id}>
                        <Send className="h-3 w-3 mr-1" />Send now
                      </Button>
                    )}
                    {isAdmin && c.status === "pending_approval" && (
                      <>
                        <Button size="sm" onClick={() => run(c.id, () => approve({ data: { campaignId: c.id } }), "Approved")} disabled={busy === c.id}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectFor(c.id); setRejectReason(""); }}>
                          <XCircle className="h-3 w-3 mr-1" />Reject
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-12">No campaigns yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject campaign</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Reason</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500} />
            <Button className="w-full" variant="destructive" disabled={!rejectReason || busy === rejectFor}
              onClick={() => {
                const id = rejectFor!;
                run(id, () => reject({ data: { campaignId: id, reason: rejectReason } }), "Rejected").then(() => setRejectFor(null));
              }}>
              Confirm rejection
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
